/**
 * How complete a workflow run is REQUIRED to be (MAR-437).
 *
 * ── ONE RUN-LEVEL FACT, REPLACING FOUR CALL-SITE FLAGS ───────────────────────────────────────────
 *
 * A run used to be allowed to be incomplete by three unrelated booleans set independently at
 * different call sites — `allowUnfilled`, `allowEmpty`, `ignoreValidations` — plus a hard `throw` in
 * `replaceDocx` that no flag could reach. Each fix revealed the next wall one station later, because
 * no layer could see what the layer above had already decided. `completeness` is that decision,
 * taken ONCE by whoever started the run, persisted on `workflow_runs`, and read wherever a
 * completeness check happens.
 *
 * ── WHAT `draft` RELAXES, AND WHAT IT NEVER RELAXES ──────────────────────────────────────────────
 *
 * COMPLETENESS only. A draft may keep its placeholders; substituting NOTHING is a valid outcome,
 * because handing back the blanks to finish in Word is the entire point of asking for one.
 *
 * It does NOT relax VALIDITY. A blocking validation failure — the Pagaré's instalment arithmetic not
 * adding up, a due date on the 31st — means the document would be legally defective, and a defective
 * instrument is refused in both modes. "Incomplete" is a document that is honestly unfinished;
 * "invalid" is one that looks finished and is wrong. Only the first is a thing a user can ask for.
 */

/** The two ways a run may be required to come out. */
export const RUN_COMPLETENESS = ['strict', 'draft'] as const

export type RunCompleteness = (typeof RUN_COMPLETENESS)[number]

/**
 * What a run is when nobody said. `strict` — the safe end: a run that nobody deliberately asked to
 * be a draft is one whose blanks are an accident.
 */
export const DEFAULT_RUN_COMPLETENESS: RunCompleteness = 'strict'

/** Read a stored/received value as a `RunCompleteness`, falling back to the strict default. */
export function asRunCompleteness(value: unknown): RunCompleteness {
  return value === 'draft' ? 'draft' : DEFAULT_RUN_COMPLETENESS
}

/** True when this run was started as a draft. */
export function isDraft(completeness: RunCompleteness | null | undefined): boolean {
  return completeness === 'draft'
}
